import React from 'react';
import { useOrderActivityLog } from '../../hooks/use-order-activity-log';
import { OrderActivityEvent } from '../../types';
import { formatDate } from '../../utils';

interface OrderActivityLogProps {
  divisionId: string;
  orderId: string;
  /** When true, the panel is visible and data is loaded */
  visible: boolean;
}

interface EventStyle {
  icon: string;
  iconCls: string;
  label: string;
}

function getEventStyle(event: OrderActivityEvent): EventStyle {
  switch (event.eventType) {
    case 'OrderSubmitted':
      return { icon: 'fa-circle-dot',    iconCls: 'text-purple-400', label: 'Order Submitted' };
    case 'ApprovalDecision': {
      const d = (event.decision ?? '').toLowerCase();
      if (d === 'approved' || d === 'accepted')
        return { icon: 'fa-circle-check',  iconCls: 'text-green-500',  label: 'Approved' };
      if (d === 'rejected' || d === 'declined')
        return { icon: 'fa-circle-xmark',  iconCls: 'text-red-500',    label: 'Rejected' };
      if (d === 'timeout')
        return { icon: 'fa-clock',         iconCls: 'text-orange-400', label: 'Timeout' };
      if (d === 'escalated')
        return { icon: 'fa-arrow-up-right-dots', iconCls: 'text-yellow-500', label: 'Escalated' };
      return { icon: 'fa-circle-half-stroke', iconCls: 'text-gray-400 dark:text-secondary-500', label: event.decision ?? 'Decision' };
    }
    case 'ApproverAdded':
      return { icon: 'fa-user-plus',      iconCls: 'text-blue-400',   label: 'Approver Added' };
    case 'EmailSent':
      return { icon: 'fa-envelope',       iconCls: 'text-sky-400',    label: 'Email Sent' };
    case 'EmailFailed':
      return { icon: 'fa-envelope-circle-exclamation', iconCls: 'text-red-500', label: 'Email Failed' };
    case 'WorkflowPaused':
      return { icon: 'fa-pause',           iconCls: 'text-yellow-500', label: 'Workflow Paused' };
    case 'WorkflowResumed':
      return { icon: 'fa-play',            iconCls: 'text-green-500',  label: 'Workflow Resumed' };
    case 'StatusChanged':
      return { icon: 'fa-rotate',         iconCls: 'text-indigo-400', label: 'Status Changed' };
    default:
      return { icon: 'fa-circle',         iconCls: 'text-gray-300 dark:text-secondary-600',   label: event.eventType };
  }
}

function EventRow({ event }: { event: OrderActivityEvent }) {
  const { icon, iconCls, label } = getEventStyle(event);

  const actor = event.actorName ?? event.actorEmail;
  const recipient = event.recipientName ?? event.recipientEmail;
  const isDecision = event.eventType === 'ApprovalDecision';
  // Backend prefixes shopper-facing EmailSent details with "Shopper:" so we can flag it distinctly
  const shopperMatch = event.details?.match(/^Shopper:\s*(.*)$/);
  const detailsText = shopperMatch ? shopperMatch[1] : event.details;

  return (
    <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-secondary-400 py-0.5">
      {/* icon */}
      <i className={`fa-solid ${icon} ${iconCls} mt-0.5 w-3.5 shrink-0`}></i>

      {/* timestamp */}
      <span className="text-gray-500 dark:text-secondary-400 shrink-0 w-[130px]">{formatDate(event.occurredAt)}</span>

      {/* server host that processed this event, in a multi-instance deployment */}
      {event.processedByHost && (
        <span
          className="px-1 py-0.5 rounded bg-gray-100 dark:bg-secondary-700 text-gray-400 dark:text-secondary-500 text-[10px] font-mono shrink-0"
          title={`Processed by server: ${event.processedByHost}`}
        >
          {event.processedByHost}
        </span>
      )}

      {/* label */}
      <span className="font-semibold text-gray-700 dark:text-secondary-200 shrink-0">{label}</span>

      {/* level badge */}
      {event.level != null && (
        <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-secondary-700 text-gray-500 dark:text-secondary-400 text-[10px] shrink-0">
          L{event.level}
        </span>
      )}

      {/* shopper badge - flags emails sent to the order requester/shopper */}
      {shopperMatch && (
        <span className="px-1.5 py-0.5 rounded bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 text-[10px] font-semibold shrink-0">
          Shopper
        </span>
      )}

      {/* For decisions: show actor prominently as "John Smith (john@example.com)" */}
      {isDecision && actor && (
        <span className="text-gray-700 dark:text-secondary-200 font-medium shrink-0">
          {actor}
          {event.actorEmail && event.actorName && (
            <span className="text-gray-400 dark:text-secondary-500 font-normal ml-1">({event.actorEmail})</span>
          )}
        </span>
      )}

      {/* For non-decision events: show actor with "by" prefix */}
      {!isDecision && actor && (
        <span className="text-gray-600 dark:text-secondary-400 shrink-0">
          by <span className="font-medium">{actor}</span>
        </span>
      )}

      {/* recipient (emails sent, approvers added) */}
      {recipient && (
        <span className="text-gray-500 dark:text-secondary-400 shrink-0">
          → <span className="font-medium">{recipient}</span>
          {event.recipientEmail && event.recipientName && (
            <span className="text-gray-400 dark:text-secondary-500 ml-1">({event.recipientEmail})</span>
          )}
        </span>
      )}

      {/* comments take priority over details for decisions */}
      {event.comments ? (
        <span className="text-gray-400 dark:text-secondary-500 italic truncate">&ldquo;{event.comments}&rdquo;</span>
      ) : detailsText ? (
        <span className="text-gray-400 dark:text-secondary-500 truncate">{detailsText}</span>
      ) : null}
    </div>
  );
}

/**
 * OrderActivityLog
 *
 * Inline admin-only panel showing a flat, time-ordered activity timeline for any order,
 * including approval decisions, emails sent to approvers, and manually-added approvers.
 */
export function OrderActivityLog({ divisionId, orderId, visible }: OrderActivityLogProps) {
  const { data: log, isLoading, isError, refetch } = useOrderActivityLog(
    divisionId,
    orderId,
    visible && !!divisionId && !!orderId
  );

  if (!visible) return null;

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-secondary-700">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 dark:text-secondary-400 uppercase tracking-wide">
          Activity Log
        </span>
        <button
          onClick={() => refetch()}
          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center gap-1"
          title="Refresh activity log"
        >
          <i className="fa-solid fa-arrow-rotate-right text-[10px]"></i>
          Refresh
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-secondary-400 py-2">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <span>Loading activity log&hellip;</span>
        </div>
      )}

      {isError && (
        <div className="text-xs text-red-600 dark:text-red-400 py-2">
          <i className="fa-solid fa-circle-exclamation mr-1"></i>
          Could not load activity log.
        </div>
      )}

      {log && !isLoading && (
        <>
          {log.events.length === 0 ? (
            <div className="text-xs text-gray-400 dark:text-secondary-500 italic py-1">No activity recorded yet.</div>
          ) : (
            <div className="space-y-0.5">
              {log.events.map((event, i) => (
                <EventRow key={i} event={event} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
